import { useState, useCallback, useMemo, type ReactNode } from 'react'
import { ReportSidebarContext, type ReportSidebarContextValue } from './ReportSidebarContextDef'
import type { BranchSelectionContextValue } from '../../../../features/branchSelection'

interface ReportSidebarProviderProps {
  children: ReactNode
  branchSelection: BranchSelectionContextValue | null
}

/**
 * Provider that owns the report sidebar visibility and selection state.
 * Children can use useReportSidebar() to control the sidebar.
 */
export function ReportSidebarProvider({ children, branchSelection }: ReportSidebarProviderProps) {
  const [isOpen, setIsOpen] = useState(false)
  const [selectedNodeIds, setSelectedNodeIds] = useState<Set<string> | null>(null)

  const open = useCallback((ids?: Set<string>) => {
    setSelectedNodeIds(ids ?? null)
    setIsOpen(true)
  }, [])

  const close = useCallback(() => setIsOpen(false), [])

  const toggle = useCallback(() => {
    setIsOpen((prev) => {
      if (prev) return false
      // When toggling open without selection, show all nodes
      setSelectedNodeIds(null)
      return true
    })
  }, [])

  const changeSelection = useCallback(() => {
    if (!branchSelection) return

    // Close sidebar, enter selection mode with current selection pre-loaded
    setIsOpen(false)
    const currentClickedIds = selectedNodeIds ? [...selectedNodeIds] : []
    branchSelection.activate({
      initialNodeIds: currentClickedIds,
      title: 'Select branches to export',
      subtitle: 'Choose which branches to include in the report',
      onConfirm: (newSelection) => {
        setSelectedNodeIds(newSelection)
        setIsOpen(true)
      },
    })
  }, [branchSelection, selectedNodeIds])

  const value = useMemo<ReportSidebarContextValue>(
    () => ({ isOpen, selectedNodeIds, open, close, toggle, changeSelection }),
    [isOpen, selectedNodeIds, open, close, toggle, changeSelection]
  )

  return <ReportSidebarContext.Provider value={value}>{children}</ReportSidebarContext.Provider>
}
